import { useEffect, useState, useCallback, useMemo } from 'react';
import { apiFetch } from '@/lib/api';
import { notify } from '@/lib/notify';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { TableCell, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { MetricGrid, SummaryMetricCard, TablePagination } from '@/components/global';
import {
  InventoryDataTableCard,
  InventoryListFiltersCard,
  InventoryRowActionsMenu,
  InventoryTableHead,
  editDeleteRowActions,
} from '@/components/inventory';
import { Plus, Search, FolderTree, Layers, Ban } from 'lucide-react';

interface Category {
  id: string;
  name: string;
  label: string | null;
  parentId: string | null;
  excludeFromMachineRag: boolean;
  sortOrder: number;
  parent?: Category | null;
  children?: Category[];
}

interface CategoryRow extends Category {
  depth: number;
  parentName: string | null;
}

interface FormData {
  name: string;
  label: string;
  parentId: string;
  excludeFromMachineRag: boolean;
  sortOrder: string;
}

const initialFormData: FormData = {
  name: '',
  label: '',
  parentId: 'none',
  excludeFromMachineRag: false,
  sortOrder: '0',
};

const PAGE_SIZE = 25;

function flattenCategories(categories: Category[]): CategoryRow[] {
  const rows: CategoryRow[] = [];
  for (const category of categories) {
    rows.push({ ...category, depth: 0, parentName: null });
    for (const child of category.children ?? []) {
      rows.push({ ...child, depth: 1, parentName: category.name });
    }
  }
  return rows;
}

export default function CategoriesPage() {
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [formData, setFormData] = useState<FormData>(initialFormData);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);

  const loadCategories = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiFetch('/api/v1/categories?includeHierarchy=true');
      if (res.ok) {
        const data = await res.json();
        setCategories(data);
      }
    } catch (err) {
      console.error('Error loading categories:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadCategories();
  }, [loadCategories]);

  const flatRows = useMemo(() => flattenCategories(categories), [categories]);

  const filteredRows = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return flatRows;
    return flatRows.filter(
      (row) =>
        row.name.toLowerCase().includes(q) ||
        (row.label?.toLowerCase().includes(q) ?? false) ||
        (row.parentName?.toLowerCase().includes(q) ?? false)
    );
  }, [flatRows, search]);

  const stats = useMemo(() => {
    const excluded = flatRows.filter((row) => row.excludeFromMachineRag).length;
    return {
      total: flatRows.length,
      topLevel: categories.length,
      excluded,
    };
  }, [flatRows, categories.length]);

  const totalPages = Math.max(1, Math.ceil(filteredRows.length / PAGE_SIZE));
  const pageRows = filteredRows.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  useEffect(() => {
    setPage(1);
  }, [search]);

  useEffect(() => {
    if (page > totalPages) {
      setPage(totalPages);
    }
  }, [page, totalPages]);

  function openCreate() {
    setEditingId(null);
    setFormData(initialFormData);
    setError(null);
    setDialogOpen(true);
  }

  function openEdit(category: Category) {
    setEditingId(category.id);
    setFormData({
      name: category.name,
      label: category.label || '',
      parentId: category.parentId || 'none',
      excludeFromMachineRag: category.excludeFromMachineRag,
      sortOrder: String(category.sortOrder),
    });
    setError(null);
    setDialogOpen(true);
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setSaving(true);

    try {
      const payload = {
        name: formData.name.trim(),
        label: formData.label.trim() || null,
        parentId: formData.parentId === 'none' ? null : formData.parentId,
        excludeFromMachineRag: formData.excludeFromMachineRag,
        sortOrder: parseInt(formData.sortOrder, 10) || 0,
      };

      if (!payload.name) throw new Error('Name is required');

      const url = editingId ? `/api/v1/categories/${editingId}` : '/api/v1/categories';
      const method = editingId ? 'PATCH' : 'POST';

      const res = await apiFetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.error || 'Failed to save category');
      }

      setDialogOpen(false);
      loadCategories();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to save category');
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(category: Category) {
    if (!window.confirm(`Delete category "${category.name}"?`)) return;
    try {
      const res = await apiFetch(`/api/v1/categories/${category.id}`, { method: 'DELETE' });
      if (!res.ok) {
        const data = await res.json();
        notify.error(data.error || 'Failed to delete category');
        return;
      }
      loadCategories();
    } catch (err) {
      console.error('Error deleting category:', err);
    }
  }

  return (
    <div className="min-h-screen bg-muted/30">
      <div className="space-y-6">
        <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
          <h1 className="text-2xl font-semibold">Categories</h1>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button onClick={openCreate}>
                <Plus className="mr-2 h-4 w-4" />
                Add Category
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>{editingId ? 'Edit Category' : 'Create Category'}</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                {error && (
                  <div className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-600">
                    {error}
                  </div>
                )}
                <div>
                  <Label htmlFor="name">Name *</Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="Category name"
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="label">Label</Label>
                  <Input
                    id="label"
                    value={formData.label}
                    onChange={(e) => setFormData({ ...formData, label: e.target.value })}
                    placeholder="Short label"
                  />
                </div>
                <div>
                  <Label htmlFor="parentId">Parent Category</Label>
                  <Select
                    value={formData.parentId}
                    onValueChange={(v) => setFormData({ ...formData, parentId: v })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="No parent (top-level)" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">No parent</SelectItem>
                      {categories
                        .filter((c) => c.id !== editingId)
                        .map((cat) => (
                          <SelectItem key={cat.id} value={cat.id}>
                            {cat.name}
                          </SelectItem>
                        ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="sortOrder">Sort Order</Label>
                  <Input
                    id="sortOrder"
                    type="number"
                    value={formData.sortOrder}
                    onChange={(e) => setFormData({ ...formData, sortOrder: e.target.value })}
                  />
                </div>
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="excludeFromMachineRag"
                    checked={formData.excludeFromMachineRag}
                    onCheckedChange={(checked) =>
                      setFormData({ ...formData, excludeFromMachineRag: Boolean(checked) })
                    }
                  />
                  <Label htmlFor="excludeFromMachineRag" className="cursor-pointer">
                    Exclude from Machine RAG calculations
                  </Label>
                </div>
                <div className="flex gap-2 pt-4">
                  <Button type="button" variant="outline" className="flex-1" onClick={() => setDialogOpen(false)}>
                    Cancel
                  </Button>
                  <Button type="submit" className="flex-1" disabled={saving}>
                    {saving ? 'Saving…' : 'Save'}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        <MetricGrid className="mb-6">
          <SummaryMetricCard label="Total categories" value={stats.total} icon={FolderTree} />
          <SummaryMetricCard label="Top-level" value={stats.topLevel} icon={Layers} />
          <SummaryMetricCard
            label="Excluded from RAG"
            value={stats.excluded}
            icon={Ban}
            status={stats.excluded > 0 ? 'amber' : undefined}
          />
        </MetricGrid>

        <InventoryListFiltersCard>
          <div className="relative min-w-[200px] flex-1">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder="Search by name, label, or parent…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9"
            />
          </div>
        </InventoryListFiltersCard>

        <InventoryDataTableCard
          loading={loading}
          isEmpty={pageRows.length === 0}
          colSpan={6}
          emptyMessage={search ? 'No categories match your search' : 'No categories yet'}
          header={
            <>
              <InventoryTableHead>Name</InventoryTableHead>
              <InventoryTableHead className="hidden md:table-cell">Label</InventoryTableHead>
              <InventoryTableHead className="hidden sm:table-cell">Parent</InventoryTableHead>
              <InventoryTableHead className="hidden md:table-cell">Exclude RAG</InventoryTableHead>
              <InventoryTableHead className="hidden lg:table-cell">Sort</InventoryTableHead>
              <InventoryTableHead className="w-[50px]" />
            </>
          }
          footer={
            !loading && filteredRows.length > 0 ? (
              <TablePagination
                page={page}
                totalPages={totalPages}
                totalItems={filteredRows.length}
                itemLabel="categories"
                pageSize={PAGE_SIZE}
                onPageChange={setPage}
              />
            ) : undefined
          }
        >
          {pageRows.map((row) => (
            <TableRow key={row.id}>
              <TableCell className="font-medium">
                <div
                  className="flex items-center gap-2"
                  style={{ paddingLeft: row.depth > 0 ? `${row.depth * 1.25}rem` : undefined }}
                >
                  {row.depth === 0 ? (
                    <FolderTree className="h-4 w-4 shrink-0 text-muted-foreground" />
                  ) : (
                    <span className="text-muted-foreground">└</span>
                  )}
                  {row.name}
                </div>
              </TableCell>
              <TableCell className="hidden md:table-cell">{row.label || '—'}</TableCell>
              <TableCell className="hidden sm:table-cell">{row.parentName || '—'}</TableCell>
              <TableCell className="hidden md:table-cell">
                {row.excludeFromMachineRag ? (
                  <Badge variant="secondary">Excluded</Badge>
                ) : (
                  <span className="text-muted-foreground">—</span>
                )}
              </TableCell>
              <TableCell className="hidden lg:table-cell">{row.sortOrder}</TableCell>
              <TableCell>
                <InventoryRowActionsMenu
                  actions={editDeleteRowActions(
                    () => openEdit(row),
                    () => handleDelete(row)
                  )}
                />
              </TableCell>
            </TableRow>
          ))}
        </InventoryDataTableCard>
      </div>
    </div>
  );
}
